<template>
	<div id="app">
		<!-- If user is logged in, keep maximum 5 components alive -->
		<keep-alive max="5" v-if="isLoggedIn">
			<router-view :key="$route.fullPath"></router-view>
		</keep-alive>

		<!-- Otherwise don't keep anything alive. -->
		<router-view v-else></router-view>
	</div>
</template>

<script>
	import _ from 'lodash';
	import CryptoJS from 'crypto-js';
	import UserAPI from '@/api/user';

	/**
	 * @module App
	 *
	 * @vue-data {boolean} [isLoggedIn=false] - Is user logged in
	 * @vue-data {array<string>} [listeners='mouseup', 'keyup'] - Events we want to listen on
	 * @vue-data {string|null} [timestamp=null] - Timestamp that will be set when an event gets triggered
	 * @vue-data {string|null} [endTimestamp=null] - expiration time for timestamp
	 * @vue-data {string|null} [token=null] - JWT token
	 */

	export default {
		name: 'App',
		data() {
			return {
				isLoggedIn: false,
				listeners: ['mouseup', 'keyup'],
				timestamp: null,
				endTimestamp: null,
				token: null,
			};
		},
		created() {
			// Assigning debounce in methods becomes an issue if we have multiple instances of a component
			// - similar to the way data should be a function that returns an object.
			// Each instance needs its own debounce function if they are supposed to act independently.
			this.handleRefreshToken = _.debounce(this.handleRefreshToken, 1000);
		},

		/**
		 * Listen for value changes in $route and isLoggedIn 
		 *
		 * @property {function} $route - Check if user is logged in
		 * @property {function} isLoggedIn - Add/remove event listeners
		 */
		watch: {
			$route() {
				if (localStorage.getItem('user') !== null) {
					this.isLoggedIn = true;
				} else {
					this.isLoggedIn = false;
				}
			},
			isLoggedIn(val) {
				if (val) {
					this.listeners.forEach(listener => {
						window.addEventListener(listener, this.handleRefreshToken, true);
					});
				} else {
					this.listeners.forEach(listener => {
						window.removeEventListener(listener, this.handleRefreshToken, true);
					});
				}
			},
		},
		methods: {
			/**
			 * @async
			 *
			 * @returns {void}
			 *
			 * @description
			 * This function gets called everytime on mouseup/keyup with a delay of 1000 ms
			 * to prevent unnecessary requests from being sent.
			 *
			 * - Define start timestamp and set it to local storage.
			 * - If there is a user to get from local storage then get its access token and timestamp.
			 * - Format the timestamp from user so it matches the start timestamp.
			 * - Get the time difference between start and end timestamp.
			 * - Set the time for when refresh token should be called which is 10 after minutes.
			 */
			async handleRefreshToken() {
				// Set the latest activity timestamp
				const startTimestamp = Math.floor(Date.now());
				localStorage.setItem('timestamp', startTimestamp);

				if (localStorage.getItem('user') !== null) {
					const { access_token, timestamp } = JSON.parse(localStorage.getItem('user'));
					const endTimestamp = timestamp * 1000;
					const remainingTime = this.calcTimestamp(endTimestamp, startTimestamp);
					const beforeExpire = 80 * 60;

					// Check to refresh token after 10 minutes of being logged in
					if (remainingTime >= 0 && remainingTime <= beforeExpire) {
						try {
							const { data } = await UserAPI.refresh(access_token);

							const userID = JSON.stringify(data.user_id);
							const companyID = JSON.stringify(data.company_id);

							const encryptedUserID = CryptoJS.AES.encrypt(
								userID,
								process.env.VUE_APP_GOOGLE_SITE_KEY
							).toString();
							const encryptedCompanyID = CryptoJS.AES.encrypt(
								companyID,
								process.env.VUE_APP_GOOGLE_SITE_KEY
							).toString();

							if (data.user_id || data.company_id) {
								delete data.user_id;
								delete data.company_id;
							}

							localStorage.setItem('user', JSON.stringify(data));
							localStorage.setItem('userID', encryptedUserID);
							localStorage.setItem('companyID', encryptedCompanyID);
						} catch (error) {
							localStorage.clear();
							this.$router.push({ path: '/login' });
						}
					}
				}
			},

			/**
			 * Calculate timestamp difference
             * 
			 * @param {number} start - Start timestamp
			 * @param {number} end - End timestamp
             * 
			 * @returns {number} - Remaining time
			 */
			calcTimestamp(end, start) {
				return Math.floor((end - start) / 1000);
			},
		},
	};
</script>
